import { useState } from "react";
import Key from "./Key";
import useKeyboardDemo from "../hooks/useKeyboardDemo";
import useMediaQuery from "../hooks/useMediaQuery";
import useLang from "../i18n/LanguageContext";
import { PROJECTS } from "./Work";
import CV from "../assets/CV/CV.pdf";
import "./Hero.css";

// Keyboard layouts. Widths are in px and add up to the same row length.
const buildRows = (compact, k, { dark, type, backspace, clear, touch, open, toggleTheme }) => {
  const letter = (id, ch, w, accent) => ({
    id,
    label: ch,
    w,
    big: true,
    kind: accent ? "accent" : "alpha",
    onPress: () => type(id, ch),
  });
  const section = (id, label, sub, w, kind, name, aria) => ({
    id,
    label,
    sub,
    w,
    kind,
    aria,
    onPress: () => {
      touch(id);
      open(name);
    },
  });
  const del = (w, sub) => ({
    id: "bs",
    label: "⌫",
    sub,
    w,
    big: true,
    kind: "mod",
    aria: k.delAria,
    onPress: () => backspace("bs"),
  });

  if (compact) {
    return [
      [letter("m1", "M", 50, true), letter("e1", "E", 50), letter("r1", "R", 50), letter("t1", "T", 50), del(106)],
      [
        letter("k2", "K", 50, true),
        letter("e2", "E", 50),
        letter("r2", "R", 50),
        letter("i2", "I", 50),
        letter("m2", "M", 50),
        letter("i3", "I", 50),
      ],
      [
        section("work", k.work, null, 96, "mod", "work", k.workAria),
        section("skills", k.skills, null, 132, "mod", "skills", k.skillsAria),
        section("cv", k.cv, null, 90, "accent", "cv", k.cvAria),
      ],
      [section("space", k.contact, null, 330, "space", "contact", k.contactAria)],
    ];
  }

  return [
    [
      {
        id: "esc",
        label: "esc",
        sub: k.clear,
        w: 88,
        kind: "mod",
        aria: k.clearAria,
        onPress: () => {
          clear("esc");
          open(null);
        },
      },
      letter("m1", "M", 88, true),
      letter("e1", "E", 88),
      letter("r1", "R", 88),
      letter("t1", "T", 88),
      del(212, k.del),
    ],
    [
      {
        id: "fn",
        label: "fn",
        sub: dark ? k.fnToLight : k.fnToDark,
        w: 120,
        kind: "mod",
        aria: dark ? k.fnToLightAria : k.fnToDarkAria,
        onPress: () => {
          touch("fn");
          toggleTheme();
        },
      },
      letter("k2", "K", 88, true),
      letter("e2", "E", 88),
      letter("r2", "R", 88),
      letter("i2", "I", 88),
      letter("m2", "M", 88),
      letter("i3", "I", 82),
    ],
    [
      section("work", k.work, "F1", 150, "mod", "work", k.workAria),
      section("skills", k.skills, "F2", 150, "mod", "skills", k.skillsAria),
      section("cv", k.cv, "pdf", 88, "accent", "cv", k.cvAria),
      section("space", k.contact, k.contactSub, 284, "space", "contact", k.contactAria),
    ],
  ];
};

const DisplayContent = ({ section, compact, t }) => {
  const d = t.display;
  switch (section) {
    case "work": {
      const latest = PROJECTS[0];
      return (
        <div className="display__panel">
          <a href={latest.url} target="_blank" rel="noopener noreferrer" className="display__row">
            <span className="ln">01 · {latest.name}</span>
            <span className="display__meta">{t.work.projects[latest.id].kind} ↗</span>
          </a>
          {!compact && (
            <a href="#work" className="display__row display__row--muted">
              <span className="ln">{d.allWork}</span>
              <span className="display__meta">{String(PROJECTS.length).padStart(2, "0")} · F1</span>
            </a>
          )}
        </div>
      );
    }
    case "skills":
      return (
        <div className="display__panel display__chips">
          {t.skills.items.map(({ name }) => (
            <span key={name} className="display__chip">{name}</span>
          ))}
        </div>
      );
    case "cv":
      return (
        <div className="display__panel display__cv">
          {!compact && <span className="display__hint">{d.cvHint}</span>}
          <a href={CV} download="Mert Kerimi CV.pdf" className="display__button" data-critter="cv">{d.cvButton}</a>
        </div>
      );
    case "contact":
      return (
        <div className="display__panel display__links">
          <a href="https://www.linkedin.com/in/mert-kerimi-853984218/" className="ln">LinkedIn ↗</a>
          <a href="https://github.com/mertkerimi" className="ln">GitHub ↗</a>
          {!compact && <a href="https://www.instagram.com/mertkerimi/" className="ln">Instagram ↗</a>}
          <a href="#contact" className="ln display__accent">{d.message}</a>
        </div>
      );
    default:
      return (
        <p className="display__panel display__hint">
          {compact ? (
            d.idleCompact
          ) : (
            <>
              {d.idle[0]}
              <strong>{t.keys.work}</strong>, <strong>{t.keys.skills}</strong>, <strong>{t.keys.cv}</strong>
              {d.idle[1]}
              <strong>{t.keys.contact}</strong>
              {d.idle[2]}
            </>
          )}
        </p>
      );
  }
};

const Hero = ({ theme, onToggleTheme }) => {
  const { t } = useLang();
  const compact = useMediaQuery("(max-width: 820px)");
  const { typed, pressed, type, backspace, clear, touch } = useKeyboardDemo({
    maxLength: compact ? 12 : 18,
  });
  const [section, setSection] = useState(null);

  const rows = buildRows(
    compact,
    { ...t.keys, fnToLightAria: t.theme.toLight, fnToDarkAria: t.theme.toDark },
    {
      dark: theme === "dark",
      type,
      backspace,
      clear,
      touch,
      open: setSection,
      toggleTheme: onToggleTheme,
    }
  );

  return (
    <section id="top" className="hero">
      <div className="hero__intro">
        <h1 className="hero__title" data-critter="title">
          {t.hero.titleBefore}
          <span className="hero__accent">{t.hero.titleAccent}</span>
          {t.hero.titleAfter}
        </h1>
        <p className="hero__lead">{t.hero.lead}</p>
        {!compact && (
          <p className="hero__hint">
            <span className="hero__kbd">{t.hero.hintKey}</span>
            {t.hero.hint}
          </p>
        )}
      </div>

      <div className="hero__device">
        <div className="display" aria-live="polite">
          <div className="display__typed">
            <span>{typed}</span>
            <span className="display__caret" aria-hidden="true" />
          </div>
          <DisplayContent key={section || "none"} section={section} compact={compact} t={t} />
        </div>

        <div className="keyboard" role="group" aria-label={t.hero.keyboard}>
          {rows.map((row, i) => (
            <div className="keyboard__row" key={i}>
              {row.map((key) => (
                <Key
                  key={key.id}
                  label={key.label}
                  sub={key.sub}
                  kind={key.kind}
                  width={key.w}
                  big={key.big}
                  pressed={pressed === key.id}
                  onPress={key.onPress}
                  ariaLabel={key.aria}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
